/**
 * Change Password Modal Component
 * Allows users to change their password
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Modal.css';

const PasswordModal = ({ isOpen, onClose, language }) => {
  const { authFetch } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Translations
  const t = {
    title: language === 'he' ? 'שינוי סיסמה' : 'Change Password',
    currentPassword: language === 'he' ? 'סיסמה נוכחית' : 'Current Password',
    newPassword: language === 'he' ? 'סיסמה חדשה' : 'New Password',
    confirmPassword: language === 'he' ? 'אימות סיסמה' : 'Confirm Password',
    save: language === 'he' ? 'שנה סיסמה' : 'Change Password',
    cancel: language === 'he' ? 'ביטול' : 'Cancel',
    saving: language === 'he' ? 'משנה...' : 'Changing...',
    savedSuccess: language === 'he' ? 'הסיסמה שונתה בהצלחה' : 'Password changed successfully',
    errorSaving: language === 'he' ? 'שגיאה בשינוי הסיסמה' : 'Error changing password',
    passwordMismatch: language === 'he' ? 'הסיסמאות לא תואמות' : 'Passwords do not match',
    passwordTooShort: language === 'he' ? 'הסיסמה חייבת להכיל לפחות 8 תווים' : 'Password must be at least 8 characters',
    show: language === 'he' ? 'הצג' : 'Show',
    hide: language === 'he' ? 'הסתר' : 'Hide'
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError('');
      setSuccess('');
      setShowPasswords({ current: false, new: false, confirm: false });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    if (formData.newPassword.length < 8) {
      setError(t.passwordTooShort);
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t.passwordMismatch);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          old_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(t.savedSuccess);
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Close after short delay
        setTimeout(() => onClose(), 1500);
      } else {
        setError(data.error || data.detail?.error || t.errorSaving);
      }
    } catch (err) {
      setError(t.errorSaving);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t.title}</h2>
          <button className="close-btn" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-group">
            <label htmlFor="currentPassword">{t.currentPassword}</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('current')}
              >
                {showPasswords.current ? t.hide : t.show}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">{t.newPassword}</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
              >
                {showPasswords.new ? t.hide : t.show}
              </button>
            </div>
            <small className="form-hint">
              {language === 'he' ? 'לפחות 8 תווים' : 'At least 8 characters'}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t.confirmPassword}</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                {showPasswords.confirm ? t.hide : t.show}
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t.cancel}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t.saving : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordModal;
